import type { GenParams, Question, Rng, Sign, Term } from './types';
import { distractors } from './distractors';
import { SIDES, comboCount, type Blockers } from './combos';
import type { Side } from './hands';

export const valueOf = (terms: readonly Term[]): number =>
  terms.reduce((s, t) => s + (t.sign === '-' ? -1 : 1) * t.a * t.b, 0);

/** How many rows and columns this term blocks: fixed counts as given, 'random' drawn 0–3; a random draw never leaves no line at all. */
function drawCounts(b: Blockers, rng: Rng): { rows: number; cols: number } {
  if (b.rows !== 'random' && b.cols !== 'random') return { rows: b.rows, cols: b.cols };
  for (let k = 0; k < 16; k++) {
    const rows = b.rows === 'random' ? rng.int(0, 3) : b.rows;
    const cols = b.cols === 'random' ? rng.int(0, 3) : b.cols;
    if (rows + cols >= 1) return { rows, cols };
  }
  return { rows: b.rows === 'random' ? 1 : b.rows, cols: b.cols === 'random' ? 1 : b.cols };
}

/** `n` distinct suit indices; a single line is one draw (keeps seeds of the one-blocker modes stable). */
function pickLines(n: number, rng: Rng): number[] {
  if (n <= 0) return [];
  if (n === 1) return [rng.int(0, 3)];
  return rng
    .shuffle([0, 1, 2, 3])
    .slice(0, n)
    .sort((x, y) => x - y);
}

/** B values chosen so that Σ B ≤ bSumMax by construction (each term leaves room for the rest). */
function sampleTerms(p: GenParams, rng: Rng): Term[] {
  const terms: Term[] = [];
  let used = 0;
  const from = { offsuit: 0, suited: 0 };
  for (let i = 0; i < p.terms; i++) {
    const remaining = p.bSumMax - used - p.bMin * (p.terms - i - 1);
    const hi = Math.min(p.bMax, remaining);
    if (hi < p.bMin) throw new Error('GenParams cannot be satisfied: bMin × terms > bSumMax');
    const b = rng.int(p.bMin, hi);
    let side: Side = rng.pick(SIDES);
    const counts = drawCounts(p.blockers, rng);
    let rows = pickLines(counts.rows, rng);
    let cols = pickLines(counts.cols, rng);
    let a = comboCount(side, rows, cols);
    // the blocked lines can leave a side with nothing (e.g. every suit blocked once): redraw, then fall back to both sides
    for (let k = 0; a < 1 && k < 8; k++) {
      rows = pickLines(counts.rows, rng);
      cols = pickLines(counts.cols, rng);
      a = comboCount(side, rows, cols);
    }
    if (a < 1) {
      side = 'both';
      a = comboCount(side, rows, cols);
    }
    const term: Term = { a, b, sign: '+', side, from: { ...from } };
    if (rows.length) term.blockedRows = rows;
    if (cols.length) term.blockedCols = cols;
    if (p.stripes) term.rank = rng.int(0, 12);
    terms.push(term);
    used += b;
    // each side continues where the last term that used it stopped (a stripe term starts its own stripe)
    if (p.stripes) continue;
    if (side !== 'suited') from.offsuit += b;
    if (side !== 'offsuit') from.suited += b;
  }
  return terms;
}

function assignSigns(terms: Term[], p: GenParams, rng: Rng): void {
  terms.forEach((t, i) => {
    t.sign = i === 0 ? '+' : (rng.pick(p.operators) as Sign);
  });
}

/**
 * Generates one question. Constraints guaranteed:
 *  - every B in [bMin, bMax], Σ B ≤ bSumMax
 *  - value ≥ resultMin (retries signs, then terms; final fallback is all '+', always valid)
 *  - each triangle keeps its own running count: a term starts where the previous term that used that side stopped
 *  - `options` distinct positive integers, correct one at a uniformly random position
 */
export function generate(p: GenParams, rng: Rng): Question {
  let terms = sampleTerms(p, rng);
  let ok = false;
  outer: for (let k = 0; k < 8; k++) {
    if (k > 0) terms = sampleTerms(p, rng);
    for (let s = 0; s < 16; s++) {
      assignSigns(terms, p, rng);
      if (valueOf(terms) >= p.resultMin) {
        ok = true;
        break outer;
      }
    }
  }
  if (!ok) terms.forEach((t) => (t.sign = '+'));

  const value = valueOf(terms);
  const wrong = distractors(terms, value, p, rng);
  const correct = rng.int(0, p.options - 1);
  const options = [...wrong];
  options.splice(correct, 0, value);
  return { terms, value, options, correct };
}
